import React, { useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import CheckFormWrapper from '../shared/CheckFormWrapper';
import FileUploadField from '../shared/FileUploadField';
import ColWithValuesInput from '../shared/ColWithValuesInput';
import TagsInput from '../shared/TagsInput';

/**
 * PrivilegeCheckForm
 *
 * Checks privileged / super-user accounts, optionally cross-referencing
 * a designation file to flag exceptions.
 *
 * API: POST /helper/privilege-check
 *
 * Props:
 *   onSubmit   - (FormData) => void
 *   isLoading  - bool
 */
export default function PrivilegeCheckForm({ onSubmit, isLoading = false }) {
  const [inputFile, setInputFile] = useState(null);
  const [usrAddrFile, setUsrAddrFile] = useState(null);
  const [designationFile, setDesignationFile] = useState(null);

  const [userNameCol, setUserNameCol] = useState([]);
  const [userNameColError, setUserNameColError] = useState('');

  const [userTypeCol, setUserTypeCol] = useState('');
  const [userTypeVals, setUserTypeVals] = useState([]);

  const [userLockCol, setUserLockCol] = useState('');
  const [userLockVals, setUserLockVals] = useState([]);

  const [userGroupCol, setUserGroupCol] = useState('');
  const [userGroupVals, setUserGroupVals] = useState([]);

  const [dateColName, setDateColName] = useState('');
  const [dateColVal, setDateColVal] = useState('');

  const [employeeNameCol, setEmployeeNameCol] = useState([]);
  const [employeeNameColError, setEmployeeNameColError] = useState('');

  const [requiredCol, setRequiredCol] = useState([]);
  const [designationCol, setDesignationCol] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    let ok = true;
    if (userNameCol.length === 0) {
      setUserNameColError('Add at least one column name');
      ok = false;
    } else {
      setUserNameColError('');
    }
    if (employeeNameCol.length === 0) {
      setEmployeeNameColError('Add at least one employee name column');
      ok = false;
    } else {
      setEmployeeNameColError('');
    }
    if (!ok) return;

    const formData = new FormData();
    formData.append('inputFile', inputFile);
    formData.append('usrAddrFile', usrAddrFile);
    if (designationFile) {
      formData.append('designationFile', designationFile);
    }

    formData.append('userNameCol', JSON.stringify(userNameCol));
    formData.append(
      'userTypeCol',
      JSON.stringify(userTypeCol ? [userTypeCol, ...userTypeVals] : [])
    );
    formData.append(
      'userLockCol',
      JSON.stringify(userLockCol ? [userLockCol, ...userLockVals] : [])
    );
    formData.append(
      'userGroupCol',
      JSON.stringify(userGroupCol ? [userGroupCol, ...userGroupVals] : [])
    );
    formData.append(
      'dateCol',
      JSON.stringify(dateColName && dateColVal ? [dateColName, dateColVal] : [])
    );
    formData.append('employeeNameCol', JSON.stringify(employeeNameCol));
    formData.append('requiredCol', JSON.stringify(requiredCol));
    formData.append('designationCol', designationCol);

    onSubmit(formData);
  };

  return (
    <CheckFormWrapper
      title="Privilege Check"
      description="Audits privileged and super-user accounts against HR data. Optionally flags exceptions using a designation file."
      onSubmit={handleSubmit}
      isLoading={isLoading}
    >
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <FileUploadField
          label="User Account Extract"
          file={inputFile}
          onChange={setInputFile}
          required
          helperText=".csv or .xlsx"
        />
        <FileUploadField
          label="User Address / HR File"
          file={usrAddrFile}
          onChange={setUsrAddrFile}
          required
          helperText=".csv or .xlsx"
        />
        <FileUploadField
          label="Designation File (optional)"
          file={designationFile}
          onChange={setDesignationFile}
          helperText=".csv or .xlsx – used to flag non-admin designations"
        />
      </Box>

      <Box>
        <TagsInput
          label={
            <>
              User Name Column <span style={{ color: '#d32f2f' }}>*</span>
            </>
          }
          values={userNameCol}
          onChange={(next) => {
            setUserNameCol(next);
            if (next.length > 0) setUserNameColError('');
          }}
          placeholder="Column name (e.g. UserID), then Enter or Add"
          helperText="First item = source column in the extract. Add a second item only if you use a separate merge column."
        />
        {userNameColError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {userNameColError}
          </Typography>
        )}
      </Box>

      <ColWithValuesInput
        label="User Type Filter (optional)"
        colLabel="User Type Column"
        valuesLabel="Allowed User Types"
        colValue={userTypeCol}
        values={userTypeVals}
        onColChange={setUserTypeCol}
        onValuesChange={setUserTypeVals}
        helperText="Leave empty to skip this filter"
      />

      <ColWithValuesInput
        label="User Lock Filter (optional)"
        colLabel="Lock Status Column"
        valuesLabel="Allowed Lock Values (integers)"
        colValue={userLockCol}
        values={userLockVals}
        onColChange={setUserLockCol}
        onValuesChange={setUserLockVals}
        valueType="number"
        helperText="Leave empty to skip this filter"
      />

      <ColWithValuesInput
        label="User Group Filter (optional)"
        colLabel="User Group Column"
        valuesLabel="Allowed Groups"
        colValue={userGroupCol}
        values={userGroupVals}
        onColChange={setUserGroupCol}
        onValuesChange={setUserGroupVals}
        helperText="Leave empty to skip this filter"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          Date Filter (optional)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Date Column"
            value={dateColName}
            onChange={(e) => setDateColName(e.target.value)}
            sx={{ minWidth: 220, maxWidth: 280 }}
          />
          <TextField
            size="small"
            label="Comparison value"
            value={dateColVal}
            onChange={(e) => setDateColVal(e.target.value)}
            sx={{ minWidth: 220, maxWidth: 280 }}
            helperText="Use the same format as in your file (e.g. 2024-06-01)"
          />
        </Box>
      </Box>

      <Box>
        <TagsInput
          label={
            <>
              Employee Name Columns <span style={{ color: '#d32f2f' }}>*</span>
            </>
          }
          values={employeeNameCol}
          onChange={(next) => {
            setEmployeeNameCol(next);
            if (next.length > 0) setEmployeeNameColError('');
          }}
          placeholder="Column name (e.g. First Name), then Enter or Add"
          helperText="First item = first-name column; add a second item for last-name column if needed."
        />
        {employeeNameColError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {employeeNameColError}
          </Typography>
        )}
      </Box>

      <TagsInput
        label="Extra Columns to Include (optional)"
        values={requiredCol}
        onChange={setRequiredCol}
        helperText="Additional column names to carry through to the output"
      />

      <TextField
        size="small"
        label="Designation Column (optional)"
        value={designationCol}
        onChange={(e) => setDesignationCol(e.target.value)}
        sx={{ maxWidth: 320 }}
        helperText="Column in the designation file used to flag non-admin accounts"
      />
    </CheckFormWrapper>
  );
}
